import { useEffect } from "react";
import { useSiteSettings } from "./useSiteSettings";
import { supabase } from "@/integrations/supabase/client";

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: (...args: unknown[]) => void;
  }
}

export const useMetaPixel = () => {
  const { data: settings } = useSiteSettings();

  useEffect(() => {
    if (!settings?.meta_pixel_id) return;

    // Initialize Meta Pixel
    const initPixel = () => {
      if (window.fbq) return; // Already initialized

      const f = window;
      const b = document;
      const e = "script";
      
      const n = function () {
        // eslint-disable-next-line prefer-rest-params
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      } as unknown as typeof window.fbq & { callMethod?: (...args: unknown[]) => void; queue: unknown[]; push: (args: unknown) => void; loaded: boolean; version: string };
      
      if (!f.fbq) f.fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      
      const t = b.createElement(e) as HTMLScriptElement;
      t.async = true;
      t.src = "https://connect.facebook.net/en_US/fbevents.js";
      
      const s = b.getElementsByTagName(e)[0];
      s.parentNode?.insertBefore(t, s);

      window.fbq("init", settings.meta_pixel_id);
      window.fbq("track", "PageView");
    };

    initPixel();
  }, [settings?.meta_pixel_id]);

  const trackEvent = async (eventName: string, eventData?: Record<string, string | number | boolean>) => {
    // Track in Meta Pixel if available
    if (window.fbq && settings?.meta_pixel_id) {
      window.fbq("track", eventName, eventData);
    }

    // Log event to database
    try {
      await supabase.from("meta_pixel_events").insert([{
        event_name: eventName,
        event_data: eventData ? JSON.parse(JSON.stringify(eventData)) : null,
        page_url: window.location.href,
      }]);
    } catch (error) {
      console.error("Failed to log pixel event:", error);
    }
  };

  return { trackEvent };
};

// Standard Meta Pixel Events
export const MetaPixelEvents = {
  PAGE_VIEW: "PageView",
  VIEW_CONTENT: "ViewContent",
  ADD_TO_CART: "AddToCart",
  INITIATE_CHECKOUT: "InitiateCheckout",
  PURCHASE: "Purchase",
  LEAD: "Lead",
  COMPLETE_REGISTRATION: "CompleteRegistration",
} as const;
